import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Modal from '@/components/modal/Modal.tsx';
import ModalHeader from '@/components/modal/ModalHeader.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import useCreateExpense from '@/features/dashboard/expenses/hooks/useCreateExpense.ts';
import {
  expenseSchema,
  ExpenseFormData,
  ExpenseInputData,
  expenseDefaultValues,
} from '@/features/dashboard/expenses/schemas/expenseSchema.ts';
import { expenseMapper } from '@/features/dashboard/expenses/types/expenseMapper.ts';
import ExpenseForm from '@/features/dashboard/expenses/components/ExpenseForm.tsx';

interface CreateExpenseModalProps {
  /** When provided, the expense is bound to this tour and the tour picker is hidden. */
  tourId?: string;
  onClose: () => void;
}

const CreateExpenseModal = ({ tourId, onClose }: CreateExpenseModalProps) => {
  const createMutation = useCreateExpense();

  const methods = useForm<ExpenseInputData>({
    resolver: zodResolver(expenseSchema),
    defaultValues: { ...expenseDefaultValues, tourId: tourId ?? '' },
  });

  const { handleSubmit } = methods;

  const onSubmit = (data: ExpenseFormData) => {
    const dto = expenseMapper.toCreateDto(data);
    createMutation.mutate(dto, { onSuccess: () => onClose() });
  };

  return (
    <Modal onClose={onClose} isDirty={methods.formState.isDirty}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader title="Zaplanuj wydatek" onClose={onClose} />
          <ModalContent>
            <ExpenseForm showTourSelect={!tourId} />
          </ModalContent>
          <ModalFooter>
            <ActionButton
              tone="ghost"
              size="sm"
              type="button"
              onClick={onClose}
            >
              Anuluj
            </ActionButton>
            <ActionButton
              tone="accent"
              size="sm"
              type="submit"
              isLoading={createMutation.isPending}
            >
              Zaplanuj wydatek
            </ActionButton>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default CreateExpenseModal;
